import { useShallow } from 'zustand/react/shallow';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { getTaskType, TYPE_PILL } from '@/utils/easykit';
import { displayProjectName } from '@/utils/format';
import { TYPE_COLORS } from './EasykitTaskCard';
import type { OdooTask, EasykitSoSummary } from '@/types/odoo';

export interface EasykitTaskDisplay {
  taskType: ReturnType<typeof getTaskType>;
  color: typeof TYPE_COLORS[keyof typeof TYPE_COLORS];
  pill: typeof TYPE_PILL[keyof typeof TYPE_PILL];
  /** Headline label — collector count for druktest, SO headline or task name otherwise. */
  primaryLabel: string;
  /** Optional `${n} collector(en)` label when soSummary present. */
  druktestLabel: string | undefined;
  /** Resolved sale-order code (e.g. `S26318454`) — empty string when absent. */
  soName: string;
  /** Resolved project name (from projectNameMap or project_id[1]) — empty string when absent. */
  projectName: string;
  /** Resolved werf address — empty string when absent. */
  address: string;
  /** Trimmed manual prefix (`x_studio_taak_prefix_1`) — empty string when absent. */
  prefix: string;
  soId: number;
  soSummary: EasykitSoSummary | undefined;
  isOnHold: boolean;
  isFeestdag: boolean;
}

/**
 * Resolve all the display-derived bits of an EK task in one place:
 * taskType + color + pill, primary label (incl. druktest collector count),
 * SO code, project name, address, prefix, on-hold / feestdag flags.
 *
 * Replaces 4–6 duplicated copies of this pattern across TaskCard,
 * TaskDragPreview, TaskDetailPanel, MoveBanner.
 */
export function useEasykitTaskDisplay(task: OdooTask): EasykitTaskDisplay {
  // Single useShallow subscription so a 30-card grid yields 30 subscribers,
  // not 90. The Maps are stable references; equality only triggers when the
  // store actually replaces them (post-fetch / silentRefresh).
  const { projectNameMap, addressMap, soSummaryMap } = useEasykitPlanningStore(
    useShallow((s) => ({
      projectNameMap: s.projectNameMap,
      addressMap: s.addressMap,
      soSummaryMap: s.soSummaryMap,
    })),
  );

  const projectId = task.project_id ? task.project_id[0] : undefined;
  const stageId = task.stage_id ? task.stage_id[0] : null;
  const soId = task.sale_order_id ? task.sale_order_id[0] : 0;
  const soSummary = soId ? soSummaryMap.get(soId) : undefined;
  const taskType = getTaskType(task.name, stageId, projectId, Boolean(soSummary));
  const color = TYPE_COLORS[taskType];
  const pill = TYPE_PILL[taskType];

  const soName = task.sale_order_id && typeof task.sale_order_id[1] === 'string' ? task.sale_order_id[1] : '';
  const projectName = projectNameMap.get(soId) || (task.project_id ? displayProjectName(task.project_id[1]) : '');
  const werfId = task.x_studio_afleveradres ? task.x_studio_afleveradres[0] : 0;
  const address = addressMap.get(werfId) || '';
  const prefix = typeof task.x_studio_taak_prefix_1 === 'string' ? task.x_studio_taak_prefix_1.trim() : '';

  const druktestLabel = soSummary
    ? `${soSummary.collectors} ${soSummary.collectors === 1 ? 'collector' : 'collectoren'}`
    : undefined;
  const primaryLabel = taskType === 'druktest'
    ? (druktestLabel ?? task.name)
    : (soSummary?.headline ?? task.name);

  return {
    taskType, color, pill,
    primaryLabel, druktestLabel,
    soName, projectName, address, prefix,
    soId, soSummary,
    isOnHold: task.x_studio_on_hold === true,
    isFeestdag: taskType === 'feestdag',
  };
}
